import { makeDeck, type Card, type Suit } from "@meksiko/shared-types";

// The engine speaks a generic English suit vocabulary (see shared-types/cards.ts);
// this is the only place that knows the actual asset filenames.
const SUIT_FILE_PREFIX: Record<Suit, string> = {
  diamonds: "karo",
  hearts: "srce",
  clubs: "tref",
  leaves: "list",
};

// Asset files number ranks 7,8,9,10,12,13,14,15 (11/Jack is skipped in the
// filename sequence, not in the rank itself) — see cards/*.png.
function fileRank(rank: Card["rank"]): number {
  return rank <= 10 ? rank : rank + 1;
}

// Prefixed with import.meta.env.BASE_URL (not a hardcoded leading "/")
// because Facebook Instant Games hosts the built bundle on a nested path,
// not the domain root — a root-absolute URL 404'd there, so no card images
// loaded at all inside the Facebook webview (reproduced live, 2026-09-07).
export function cardImageUrl(card: Card): string {
  return `${import.meta.env.BASE_URL}cards/${SUIT_FILE_PREFIX[card.suit]}_${fileRank(card.rank)}.png`;
}

export const CARD_BACK_URL = `${import.meta.env.BASE_URL}cards/back.svg`;

// Nothing fetched a card image before it was first needed on screen, so a
// player's first deal always paid the full network cost of every card image
// it used, one request at a time — slow and very noticeable on mobile
// connections (reported live, 2026-09-07). Call this once at boot (see
// main.ts) to warm the browser's HTTP cache for all 32 faces + the back
// while the player is still in the lobby, well before a deal needs them.
export function preloadCardImages(): void {
  for (const card of makeDeck()) {
    new Image().src = cardImageUrl(card);
  }
  new Image().src = CARD_BACK_URL;
}

// Matches the actual fill colors used in cards/*.png (a red/black deck).
export const SUIT_COLOR: Record<Suit, string> = {
  diamonds: "#ff8181",
  hearts: "#ff8181",
  clubs: "#1a1a1a",
  leaves: "#1a1a1a",
};

// SUIT_COLOR's near-black clubs/leaves is correct against a card's white
// face, but illegible as text/border color on this app's dark felt panels.
// Use this instead for any suit-colored UI chrome drawn directly on the felt
// (the trump indicator) — trump-choice buttons sit on a light gold
// background so SUIT_COLOR is fine there.
export const SUIT_UI_COLOR: Record<Suit, string> = {
  diamonds: "#ff8181",
  hearts: "#ff8181",
  clubs: "#f4ead3",
  leaves: "#f4ead3",
};

export function rankLabel(rank: Card["rank"]): string {
  switch (rank) {
    case 11:
      return "J";
    case 12:
      return "Q";
    case 13:
      return "K";
    case 14:
      return "A";
    default:
      return String(rank);
  }
}
